import React, { useEffect, useState } from "react";
import { 
    Box, Container, Grid, LinearProgress, Card, Rating, Typography, 
    Stack, Avatar, Divider 
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faCommentDots, faChartBar, faCommentSlash } from "@fortawesome/free-solid-svg-icons";

// Componentes QUD
import CustomBreadcrumbs from "../../../components/breadcrumbs";
import QUDProgress from "../../../components/progress";
import ReviewsDataTable from "./dataTable";
import { getAll } from "../../../controllers/company/ReviewsController";

export default function Reviews() {
    const { t } = useTranslation();

    const [reviews, setReviews] = useState([]);
    const [reviewsCount, setReviewsCount] = useState(0);
    const [reviewsAvg, setReviewsAvg] = useState(0);
    const [reviewsValue, setReviewsValue] = useState([]);

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        handleGetReviews();
    }, []);

    const handleGetReviews = async () => {
        setIsLoading(true);
        let response = await getAll();
        if (response.success === true) {
            setReviews(response.data.reviews || []);
            setReviewsCount(response.data.reviews_count || 0);
            setReviewsAvg(response.data.reviews_avg || 0);
            setReviewsValue(response.data.reviews_values || []);
        }
        setIsLoading(false);
    }

    // Estilo unificado para las tarjetas (Limpio y moderno)
    const cardStyle = {
        elevation: 0,
        sx: { border: '1px solid #e0e0e0', borderRadius: 3, p: 3, bgcolor: '#ffffff', height: '100%' }
    };

    return (
        <Container maxWidth='xl' sx={{ py: 4 }}>
            <QUDProgress open={isLoading && reviews.length === 0} />

            <CustomBreadcrumbs returnTo='/' crumbs={[{ name: `${t('reviews.title')}`, link: '/reviews' }]} />
            
            {/* HEADER PRINCIPAL */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 4 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#222' }}>
                        {t('reviews.panel') || 'Panel de Reseñas'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                        Conoce lo que tus clientes opinan sobre tus servicios y productos.
                    </Typography>
                </Box>
                {/* Aquí no ponemos botón de "Crear" porque las reseñas las hacen los clientes */}
            </Box>

            {/* CABECERA DE ESTADÍSTICAS (KPIs) */}
            {(reviews.length > 0 || isLoading) && (
                <Grid container spacing={3} mb={4}>
                    
                    {/* KPI 1: Total de Reseñas */}
                    <Grid item xs={12} md={4}>
                        <Card {...cardStyle} sx={{ ...cardStyle.sx, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 50, height: 50, bgcolor: '#e3f2fd', color: '#1976d2', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faCommentDots} size="lg" />
                                </Avatar>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        {t('reviews.totalReviews') || 'Total Reseñas'}
                                    </Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#222', my: 0.2 }}>
                                        {reviewsCount}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('reviews.totalReviewsYear') || 'En el último año'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    {/* KPI 2: Promedio de Calificación */}
                    <Grid item xs={12} md={4}>
                        <Card {...cardStyle} sx={{ ...cardStyle.sx, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 50, height: 50, bgcolor: '#fff8e1', color: '#ffb300', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faStar} size="lg" />
                                </Avatar>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        {t('reviews.averageRating') || 'Calificación Promedio'}
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={1} my={0.5}>
                                        <Typography variant="h3" sx={{ fontWeight: 800, color: '#222' }}>
                                            {reviewsAvg}
                                        </Typography>
                                        <Rating precision={0.5} value={parseFloat(reviewsAvg)} readOnly size="medium" sx={{ color: '#ffb300' }} />
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('reviews.averageRatingYear') || 'En el último año'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    {/* KPI 3: Distribución de Estrellas (Barras de progreso) */}
                    <Grid item xs={12} md={4}>
                        <Card {...cardStyle} sx={{ ...cardStyle.sx, p: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                                <FontAwesomeIcon icon={faChartBar} color="#888" size="sm" />
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Distribución
                                </Typography>
                            </Stack>
                            
                            <Box sx={{ flexGrow: 1 }}>
                                {reviewsValue.length > 0 ? reviewsValue.map((item) => {
                                    // Prevención de NaN si reviewsCount es 0
                                    const percentage = reviewsCount > 0 ? (item.value / reviewsCount) * 100 : 0;
                                    return (
                                        <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', mb: 0.8 }}>
                                            <Box sx={{ minWidth: 20, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body2" fontWeight="bold" color="text.secondary">{item.label}</Typography>
                                            </Box>
                                            <Box sx={{ width: '100%', mx: 1.5 }}>
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={percentage} 
                                                    sx={{ 
                                                        height: 8, 
                                                        borderRadius: 4, 
                                                        bgcolor: '#f0f0f0', 
                                                        '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#ffb300' } // Color dorado Google/Amazon
                                                    }} 
                                                />
                                            </Box>
                                            <Box sx={{ minWidth: 25, textAlign: 'right' }}>
                                                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                                    {item.value}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                }) : (
                                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                        Aún no hay datos.
                                    </Typography>
                                )}
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* CONTENEDOR PRINCIPAL DE LA TABLA */}
            {reviews.length !== 0 ? (
                // Renderizado directo del componente de tabla de reseñas
                <ReviewsDataTable reviews={reviews} />
            ) : (
                !isLoading && (
                    /* EMPTY STATE (Estado vacío cuando no hay reseñas) */
                    <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 3, boxShadow: 'none', py: 10, mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <Box sx={{ width: 80, height: 80, bgcolor: '#f8f9fa', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                            <FontAwesomeIcon icon={faCommentSlash} size="3x" color="#ccc" />
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: '#333' }} gutterBottom>
                            Aún no tienes reseñas
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', maxWidth: 450 }}>
                            Tus clientes podrán calificar sus pedidos y reservas. Una vez que comiencen a dejar sus opiniones, aparecerán aquí.
                        </Typography>
                    </Card>
                )
            )}
        </Container>
    );
}